/*!

 =========================================================
 * Vue Paper Dashboard - v1.0.1
 =========================================================

 * Product Page: http://www.creative-tim.com/product/paper-dashboard
 * Copyright 2023 Creative Tim (http://www.creative-tim.com)
 * Licensed under MIT (https://github.com/creativetimofficial/paper-dashboard/blob/master/LICENSE.md)

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

 */
// Neutralise console.log/info/debug en production avant tout autre import,
// afin qu'aucun payload (response.data, formData, etc.) ne fuite dans la console
// du navigateur. console.warn / console.error restent actifs pour le support
// MAIS sont enveloppés pour scrubber les AxiosError (config.data = corps de
// requête, response.data = corps de réponse renvoyé par gestion/views ou
// gwem_backend/views, headers Authorization, etc.).
// Override-able : window.localStorage.setItem('debug_console', '1') pour réactiver
// temporairement en cas d'investigation.
(function silenceConsoleInProduction() {
  try {
    const isProd =
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV === 'production';
    if (!isProd) return;
    let force = null;
    try { force = window.localStorage.getItem('debug_console'); } catch (_) { force = null; }
    const host = String(window.location?.hostname || '').toLowerCase();
    const localDev = host === 'localhost' || host === '127.0.0.1';
    if (force === '1' && localDev) return;
    const noop = function () {};
    ['log', 'info', 'debug', 'trace'].forEach((m) => {
      try { if (typeof console !== 'undefined' && console[m]) console[m] = noop; } catch (_) {}
    });

    // Wrap console.error / console.warn : on les conserve (utiles au support)
    // mais on remplace toute AxiosError par un résumé sûr — sans corps de
    // requête/réponse, sans headers, sans tokens.
    const isAxiosError = function (v) {
      try {
        if (!v || typeof v !== 'object') return false;
        if (v.isAxiosError === true) return true;
        // Heuristique : présence simultanée de config + (response || request)
        return !!(v.config && (v.response || v.request));
      } catch (_) { return false; }
    };
    const safeStatus = function (v) {
      try { return v && v.response && (v.response.status || v.response.statusCode); } catch (_) { return undefined; }
    };
    const safeUrl = function (v) {
      try {
        const c = v && v.config;
        if (!c) return undefined;
        const u = String(c.url || '');
        // Retire querystring qui peut contenir des identifiants
        return u.split('?')[0];
      } catch (_) { return undefined; }
    };
    const safeMethod = function (v) {
      try { return v && v.config && v.config.method && String(v.config.method).toUpperCase(); } catch (_) { return undefined; }
    };
    const sanitize = function (arg) {
      if (!isAxiosError(arg)) return arg;
      // Ne renvoyer qu'un résumé : pas de data, pas de headers, pas de stack.
      return {
        __axios_error__: true,
        name: arg.name || 'AxiosError',
        message: typeof arg.message === 'string' ? arg.message : '[error]',
        code: arg.code,
        method: safeMethod(arg),
        url: safeUrl(arg),
        status: safeStatus(arg),
      };
    };
    ['warn', 'error'].forEach((m) => {
      try {
        if (typeof console === 'undefined' || !console[m]) return;
        const orig = console[m].bind(console);
        console[m] = function () {
          try {
            const args = new Array(arguments.length);
            for (let i = 0; i < arguments.length; i++) args[i] = sanitize(arguments[i]);
            return orig.apply(console, args);
          } catch (_) {
            // En cas de souci, on n'affiche rien plutôt que de risquer une fuite.
          }
        };
      } catch (_) {}
    });
  } catch (_) { /* never break boot */ }
})();

import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/index";
import axios from "axios";
import './assets/css/tokens.css';
import './assets/css/components.css';
import './main.css';
import './bootstrap.min.css';
import './assets/css/tailwind-dashboard.css';
import './assets/css/banking-theme.css';
import './assets/css/insurance-premium.css';
import './assets/css/module2-patrimonea.css';
import './assets/css/client-space.css';
import './assets/css/staff-space.css';
import './assets/css/mandat-space.css';
import './assets/css/responsive.css';
import './assets/js/bootstrap.min';
import './assets/css/Filter.css';

import PaperDashboard from "./plugins/paperDashboard";

import InactivityPlugin from "./plugins/inactivityPlugin";
import eventBus from "./plugins/eventBus";
import { maybeRefreshCachesOnNewVersion } from "@/utils/browserCache";
import { resolveApiBaseUrl } from "@/utils/apiOrigin";
import {
  installAxiosAuth,
  isAuthenticatedSession,
  migrateLegacyProfileToSession,
  purgeLegacyJwtStorage,
  verifyServerSession,
} from "@/utils/sessionAuth";
import { initAnalytics } from "@/utils/analytics";

// Définir un timeout global plus tolérant sur l'environnement test.
// Le backend peut répondre de manière intermittente >30s pendant les phases de warmup.
axios.defaults.timeout = 45000; // 45s (évite de bloquer l'UI trop longtemps)

// Frontends déployés → proxy Static Web Apps /api vers le BFF gestion Azure.
const apiBase = resolveApiBaseUrl();
if (apiBase) {
  axios.defaults.baseURL = apiBase;
  if (typeof window !== 'undefined') window.__API_BASE__ = apiBase;
}

// Cookies httpOnly + CSRF (pas de JWT en localStorage)
installAxiosAuth(axios, { router });

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    try {
      const config = error?.config;
      const method = String(config?.method || '').toLowerCase();
      const status = error?.response?.status;
      const code = error?.code;

      const shouldRetry =
        method === 'get' &&
        config &&
        (code === 'ECONNABORTED' ||
          String(error?.message || '').toLowerCase().includes('timeout') ||
          status === 503);

      const retryCount = config?.__retryCount || 0;
      const maxRetries = 3;
      if (shouldRetry && retryCount < maxRetries) {
        config.__retryCount = retryCount + 1;
        const retryTimeout = Math.min(45000, 25000 + 10000 * config.__retryCount);
        config.timeout = retryTimeout;
        const backoffMs = 600 * config.__retryCount;
        return new Promise((resolve) => setTimeout(resolve, backoffMs)).then(() => axios.request(config));
      }
    } catch (_) { /* ignore */ }
    return Promise.reject(error);
  }
);

/* eslint-disable no-new */
async function bootstrap() {
  const reloading = await maybeRefreshCachesOnNewVersion();
  if (reloading) return;

  initAnalytics();

  purgeLegacyJwtStorage();
  migrateLegacyProfileToSession();
  if (isAuthenticatedSession()) {
    await verifyServerSession(axios);
  }

  const app = createApp(App);

  // Configuration globale pour les notifications
  app.config.globalProperties.$notificationStore = {
    notifications: [],
    addNotification(n) {
      try {
        this.notifications.push(n);
      } catch (_) {}
    },
    removeNotification(n) {
      try {
        const i = this.notifications.indexOf(n);
        if (i > -1) this.notifications.splice(i, 1);
      } catch (_) {}
    }
  };

  app.use(router);
  app.use(PaperDashboard);
  app.use(InactivityPlugin, { router });
  app.config.globalProperties.$bus = eventBus;
  app.mount("#app");
}

bootstrap();

// Réduire le bruit TensorFlow.js / face-api (kernels déjà enregistrés, etc.) une fois tf chargé
function silenceTfJsWarnings() {
  try {
    if (typeof window !== 'undefined' && window.tf && window.tf.env) {
      window.tf.env().set('CPP_MIN_LOG_LEVEL', 2);
    }
  } catch (_) {}
}
setTimeout(silenceTfJsWarnings, 500);
if (typeof window !== 'undefined') window.addEventListener('load', silenceTfJsWarnings);
